import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        this.#morphFromSectionHTML(parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphFromSectionHTML(cartItemsHtml);
    } else {
      sectionRenderer
        .getSectionHTML(this.sectionId, false)
        .then((sectionHtml) => this.#morphFromSectionHTML(sectionHtml))
        .catch((error) => {
          console.error(error);
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        });
    }
  };

  /**
   * Morph only the cart-items component subtree, not the whole section/dialog.
   * This avoids replacing an open <dialog> and losing its native backdrop state.
   * @param {string} sectionHtml
   */
  #morphFromSectionHTML(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return;

    const newSectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const selector = `cart-items-component[data-section-id="${this.sectionId}"]`;
    const newCartItemsComponent = newSectionDocument.querySelector(selector);

    if (!(newCartItemsComponent instanceof HTMLElement)) {
      // Fallback to previous behavior if subtree is missing for any reason.
      morphSection(this.sectionId, sectionHtml);
      return;
    }

    morph(this, newCartItemsComponent);

    // La classe `cart-drawer--empty` est posée côté serveur sur le <dialog> au
    // chargement et n'est pas couverte par le morph (childrenOnly ne touche que
    // les enfants de cart-items-component). On la resynchronise avec l'état
    // renvoyé par le serveur, sinon le panier garde le rendu « vide » lors du
    // premier ajout (passage vide -> rempli).
    const liveDialog = this.closest('dialog');
    const newDialog =
      newCartItemsComponent.closest('dialog') ?? newSectionDocument.querySelector('.cart-drawer__dialog');

    if (liveDialog && newDialog) {
      liveDialog.classList.toggle('cart-drawer--empty', newDialog.classList.contains('cart-drawer--empty'));
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

function getCartSectionsContext() {
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter((sectionId) => typeof sectionId === 'string' && sectionId !== '');

  return {
    sectionIds,
    sections: Array.from(new Set(sectionIds)).join(','),
    sectionsUrl: window.location.pathname,
  };
}

function dispatchCartSectionsUpdate(sections, source = 'theme-cart-sync') {
  if (!sections || typeof sections !== 'object') return;

  document.dispatchEvent(
    new CustomEvent(ThemeEvents.cartUpdate, {
      bubbles: true,
      detail: {
        resource: null,
        sourceId: 'theme-cart-sync',
        data: {
          source,
          sections,
        },
      },
    })
  );
}

async function dispatchCartUpdatedWindowEvent(cartPayload) {
  try {
    const hasItemsArray = Array.isArray(cartPayload?.items);
    let cart;
    if (hasItemsArray) {
      cart = cartPayload;
      window.ThemeCartSync?.invalidateCart?.(cart);
    } else if (typeof window.ThemeCartSync?.getCart === 'function') {
      cart = await window.ThemeCartSync.getCart({ force: true });
    } else {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      cart = await response.json();
    }
    if (!cart) return;
    window.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
  } catch (error) {}
}

if (typeof window !== 'undefined') {
  // Extend rather than replace so the inline cache helper (getCart / invalidateCart)
  // defined in scripts.liquid is preserved.
  window.ThemeCartSync = Object.assign(window.ThemeCartSync || {}, {
    getSectionsContext: getCartSectionsContext,
    emitSectionsUpdate: dispatchCartSectionsUpdate,
    emitCartUpdated: dispatchCartUpdatedWindowEvent,
  });
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
